<template>
  <div class="select-products-dialog mt-5">
    <div>
      <mf-search
        placeholder="Busque produtos ou grupos"
        dense
        :disabled="chosenLoader"
        :value="searchValue"
        class="mb-5"
        autofocus
        hide-details
        :loading="loading"
        @input="querySelections"
      />

      <div class="d-flex justify-space-between align-center mt-5">
        <h2 class="container-subtitle">Resultados ({{ mergedSearchedItemsAndGroups.length }})</h2>
        <span v-if="searchedItems.length" :class="[isSelectAllEnabled ? 'link' : 'disabled-link']" @click="selectAllSearchedItems(searchedItems, chosenItems)">
          Selecionar todos produtos
        </span>
      </div>

      <div class="products-box left-side">
        <div v-if="loading" class="products-box-empty-state">
          <v-progress-circular size="20" indeterminate color="primary" class="mt-4" />
        </div>

        <div v-else-if="!mergedSearchedItemsAndGroups.length && !loading" class="products-box-empty-state">
          <mf-icon icon="search" size="24" color="cinza-l20" />
          <p>Busque um produto para exibir os resultados</p>
        </div>

        <DynamicScroller
          v-else
          v-slot="{ item, index, active }"
          class="vue-recycle-scroller scroller ready direction-vertical"
          :items="mergedSearchedItemsAndGroups"
          :min-item-size="20"
          key-field="_id"
        >
          <DynamicScrollerItem :item="item" :active="active" :data-index="index">
            <mf-tooltip top variant="secondary" max-width="300px">
              <template #activator>
                <div
                  class="result-list-item d-flex align-center justify-space-between"
                  @click="() => (isGroup(item) ? checkGroupProducts(item, productsGroups, chosenItems) : setItem(item, searchedItems, chosenItems))"
                >
                  <div class="d-flex align-center product-name-container">
                    <mf-icon v-if="isGroup(item)" fill color="marca-1" icon="folder" />
                    <span>{{ isGroup(item) ? `${item.name}` : `${item.sku} - ${item.name}` }}</span>
                  </div>
                  <mf-icon fill color="cinza-0" icon="chevron_right" size="20" />
                </div>
              </template>
              {{ item.name }}
            </mf-tooltip>
          </DynamicScrollerItem>
        </DynamicScroller>
      </div>
    </div>

    <div>
      <div class="filter-button ml-4">
        <filters-menu activator-type="button" :filters="['measurementUnits']" skip-mount-side-effects />
      </div>

      <h2 class="container-subtitle mt-5 ml-4">Selecionados ({{ chosenItems.length }})</h2>

      <div class="products-box right-side">
        <div v-if="chosenLoader" class="d-flex align-center justify-center">
          <v-progress-circular size="20" indeterminate color="primary" class="mt-4" />
        </div>

        <div v-else-if="!chosenItems.length && !chosenLoader" class="products-box-empty-state">
          <mf-icon fill icon="folder_open" size="24" color="cinza-l20" />
          <p>Nenhum produto selecionado</p>
        </div>

        <div v-else-if="showProcessingIndicator && isProcessingItems && chosenItems.length > 1000" class="processing-indicator">
          <v-progress-circular size="20" indeterminate color="primary" class="mr-2" />
          <span>Processando {{ chosenItems.length }} produtos selecionados...</span>
        </div>

        <div class="right-side-items-list">
          <p v-if="!chosenLoader && chosenItems.length" class="clear-all mb-0" @click="clearAll">Limpar todos</p>
          <DynamicScroller
            v-if="chosenItems.length"
            ref="selectedScroller"
            v-slot="{ item, index, active }"
            class="vue-recycle-scroller selected-items-scroller ready direction-vertical"
            :items="formattedChosenItems"
            :min-item-size="20"
            :buffer="20"
            :page-mode="false"
            key-field="_id"
            :prerender="3"
            :emit-update="false"
            :emit-resize="false"
          >
            <DynamicScrollerItem :ref="'group-' + item._id" :item="item" :active="active" :data-index="index">
              <div v-if="!item.isGroup">
                <mf-tooltip top variant="secondary" max-width="300px">
                  <template #activator>
                    <div class="d-flex align-center normal-product">
                      <p>{{ item.sku }} - {{ item.name }}</p>
                      <v-btn v-if="!item.disabled" icon @click.stop.prevent="setItem(item, chosenItems, searchedItems, true)">
                        <mf-icon fill color="cinza-d0" icon="close" size="20" />
                      </v-btn>
                    </div>
                  </template>
                  {{ item.name }}
                </mf-tooltip>
              </div>

              <div v-else class="group-container my-1">
                <mf-tooltip top variant="secondary" max-width="300px">
                  <template #activator>
                    <div class="d-flex align-center normal-product justify-space-between">
                      <div class="d-flex align-center">
                        <p>{{ item.name }}</p>
                        <v-btn v-if="!item.disabled" class="ml-1" icon @click.stop.prevent="removeGroup(item, chosenItems, searchedItems)">
                          <mf-icon fill color="cinza-d0" icon="close" size="20" />
                        </v-btn>
                      </div>
                      <v-btn icon @click.stop.prevent="toggleGroupExpand(item._id)">
                        <mf-icon
                          fill
                          color="cinza-d0"
                          icon="keyboard_arrow_down"
                          size="24"
                          :class="{ rotated: isGroupExpanded(item._id), 'cursor-pointer': true }"
                        />
                      </v-btn>
                    </div>
                  </template>
                  {{ item.name }}
                </mf-tooltip>

                <DynamicScroller
                  v-if="isGroupExpanded(item._id)"
                  v-slot="{ item: product, index: pIndex, active: pActive }"
                  class="group-products-scroller"
                  :items="getVisibleGroupProducts(item)"
                  :min-item-size="20"
                  :buffer="20"
                  :page-mode="false"
                  key-field="_id"
                  :prerender="3"
                >
                  <DynamicScrollerItem :item="product" :active="pActive" :data-index="pIndex" :size-dependencies="[isGroupExpanded(item._id)]">
                    <mf-tooltip top variant="secondary" max-width="300px">
                      <template #activator>
                        <div class="d-flex align-center normal-product px-5">
                          <p>{{ product.sku }} - {{ product.name }}</p>
                          <v-btn v-if="!item.disabled" icon @click.stop.prevent="setItem(product, chosenItems, searchedItems, true)">
                            <mf-icon fill color="cinza-d0" icon="close" size="20" />
                          </v-btn>
                        </div>
                      </template>
                      {{ product.name }}
                    </mf-tooltip>
                  </DynamicScrollerItem>
                </DynamicScroller>
              </div>
            </DynamicScrollerItem>
          </DynamicScroller>
        </div>
      </div>
    </div>
    <mf-toggle v-model="sortByRelevance" label="Ordenar produtos por relevância de venda" class="pl-1 mt-4" hide-details />
  </div>
</template>

<script>
import { QUERY_GET_GROUP_PRODUCTS } from '@/modules/produtos/gerenciamento-produtos/graphql'
import { QUERY_GET_PRODUCTS_AND_GROUPS, PRODUCTS_SUBSCRIPTION, QUERY_ACTIVATE_IMPORTED_PRODUCTS } from '@/modules/produtos/graphql'
import findIndex from 'lodash/findIndex'
import find from 'lodash/find'
import capitalize from 'lodash/capitalize'
import CSV from 'csv-string'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { v4 as uuidv4 } from 'uuid'
import { wsClient } from '@/services/webSocket'
import { mapState } from 'vuex'
export default {
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    FiltersMenu: () => import('@/components/FiltersMenu.vue'),
  },
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    offerType: {
      type: String,
      default: '',
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    filterDuplicateProducts: {
      type: Boolean,
      default: true,
    },
    hideCsvUpload: {
      type: Boolean,
      default: false,
    },
    disableGroups: {
      type: Boolean,
      default: false,
    },
    defaultImport: {
      type: Boolean,
      default: true,
    },
    isLengthLimited: {
      type: Boolean,
      default: false,
    },
    maxSelectedItems: {
      type: Number,
      default: 0,
    },
    window: {
      type: Number,
      default: 0,
    },
    titleDialog: {
      type: String,
      default: '',
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    onlyActive: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    roomId: '',
    searchValue: '',
    sortByRelevance: true,
    loading: false,
    chosenLoader: false,
    searchedItems: [],
    chosenItems: [],
    chosenGroupsItems: [],
    productsGroups: [],
    capitalize: capitalize,
    fileContent: null,
    fileTypes: [
      'text/comma-separated-values',
      'text/csv',
      'application/csv',
      'application/excel',
      'application/vnd.ms-excel',
      'application/vnd.msexcel',
      'text/anytext',
      '',
    ],
    expandedGroups: {},
    formattedChosenItemsCache: null,
    chosenItemsLength: 0,
    debounceTimer: null,
    isProcessingItems: false,
    persistentCache: null, // Cache that persists across modal opens/closes
    cacheKey: null, // Key to identify cached data
    cacheVersion: 0, // Version counter to invalidate cache after confirmations
    showProcessingIndicator: false,
    // Track insertion order for individual products so newly added appear on top (below groups)
    insertionOrderMap: {},
    insertionCounter: 0,
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    // return an array of items based on the chosen products, grouped by group
    formattedChosenItems() {
      // If no chosen items, return empty array
      if (!this.chosenItems.length) {
        return []
      }

      // Check persistent cache first for instant loading
      const currentCacheKey = this.generateCacheKey()
      if (this.persistentCache && this.persistentCache.version === this.cacheVersion && this.cacheKey === currentCacheKey && this.chosenItems.length > 0) {
        // Only use persistent cache if version matches and it has the same or more items than chosenItems
        if (this.persistentCache.data && this.persistentCache.data.length >= this.chosenItems.length) {
          return this.persistentCache.data
        } else {
          // Clear the outdated cache
          this.clearPersistentCache()
        }
      } else if (this.persistentCache && (this.cacheKey !== currentCacheKey || this.persistentCache.version !== this.cacheVersion)) {
        this.clearPersistentCache()
      }

      // Use regular cache to avoid recalculating on every access
      if (this.formattedChosenItemsCache && this.chosenItemsLength === this.chosenItems.length) {
        return this.formattedChosenItemsCache
      }

      // Always process synchronously to keep UI stable, even for large lists
      // This ensures that when items are added/removed, the list stays static
      // instead of appearing to reload/scroll
      return this.processItemsSync()
    },
    isOffersByUnit() {
      return this.$getAllowedModules()['offers_by_unit'] && this.offerType === 'dynamic_pricing_percentage_offer_sales'
    },
    isSelectAllEnabled() {
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems) {
        return false
      }
      if (!this.searchedItems.length) {
        return false
      }
      return true
    },
    mergedSearchedItemsAndGroups() {
      return [...this.productsGroups, ...this.searchedItems]
    },
  },
  watch: {
    sortByRelevance() {
      this.searchItem(this.searchValue)
    },
    window: {
      handler(newValue) {
        if (newValue && newValue === 1) {
          // Modal is opening - always validate and potentially reprocess
          // This ensures that if selectedProducts changed via .sync while modal was closed,
          // we detect and process the changes correctly
          const currentCacheKey = this.generateCacheKey()

          // Always check if products changed while modal was closed OR cache version changed
          // This is critical for .sync scenarios where watchers may not fire
          // Also check version to ensure cache wasn't invalidated by a previous confirmation
          const cacheVersionMismatch = this.persistentCache && this.persistentCache.version !== this.cacheVersion
          if (this.cacheKey && (this.cacheKey !== currentCacheKey || cacheVersionMismatch)) {
            // Products changed or cache was invalidated - clear cache
            this.clearPersistentCache()
            this.chosenItemsLength = 0
          }

          // Set selected items (will use cache if valid, or process fresh if cache was cleared)
          this.setSelectedItems()
        } else if (newValue === 0) {
          // Modal is closing, pause processing and clear temporary data
          this.pauseProcessing()
          // Clear temporary cache but keep persistent cache
          // NOTE: We keep chosenItemsLength to help detect changes on next open
          this.formattedChosenItemsCache = null
        }
      },
    },
    chosenLoader(newValue) {
      this.$emit('change-import-loader', newValue)
    },
    'stateFilters.measureUnit'() {
      this.querySelections(this.searchValue)
    },
    chosenItems: {
      handler(newValue, oldValue) {
        // Clear cache when chosenItems changes
        this.clearCache()

        // If chosenItems is being cleared (becoming empty), clear persistent cache too
        if (newValue.length === 0 && oldValue && oldValue.length > 0) {
          this.clearPersistentCache()
        }
      },
      deep: true,
    },
    // Propagate processing state to parent modal to control Confirm button
    isProcessingItems(newValue) {
      this.$emit('change-processing', newValue)
    },
    selectedProducts: {
      handler(newValue, oldValue) {
        // Clear cache if selectedProducts changed (different content, not just length)
        const currentCacheKey = this.generateCacheKey()
        if (this.cacheKey && this.cacheKey !== currentCacheKey) {
          // Cache key changed, meaning products changed - clear cache
          this.clearPersistentCache()
        }

        // Only preload data when selected products change and we have more items
        // Don't preload if we're losing items (like when sync updates to fewer items)
        if (this.selectedProducts.length > 0 && this.selectedProducts.length >= (oldValue ? oldValue.length : 0)) {
          this.debouncedOperation(() => {
            this.preloadData()
          }, 500)
        }
      },
      deep: true,
    },
  },
  async mounted() {
    const roomId = uuidv4()
    this.roomId = roomId
    this.setSelectedItems()

    const self = this
    await wsClient.subscribe(
      {
        query: PRODUCTS_SUBSCRIPTION,
        operationName: 'ValidImportedProducts',
        variables: {
          roomId: this.roomId,
        },
      },
      {
        next({ data: { validImportedProducts } }) {
          const { availableTotal, data, deliveredTotal } = validImportedProducts

          if (data?.length > 0) {
            data.forEach((item) => {
              const indexOnSearched = findIndex(self.searchedItems, item)
              const indexOnChosen = findIndex(self.chosenItems, item)
              if (indexOnChosen > -1) return
              self.searchedItems.splice(indexOnSearched, 1)
            })
            const filterProducts = self.removeChosenItems(data)

            self.chosenItems.push(...filterProducts)
            self.chosenGroupsItems.push(...filterProducts)
          }
          if (availableTotal === deliveredTotal) self.chosenLoader = false
        },
        error(error) {
          console.error(error)
          self.chosenLoader = false
          self.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
        },
      }
    )
  },
  methods: {
    getItemKey(item) {
      return item && (item._id || item.sku)
    },
    markInserted(item) {
      const key = this.getItemKey(item)
      if (!key) return
      // Always assign a fresh order when item is explicitly added by the user
      this.insertionCounter += 1
      this.insertionOrderMap[key] = this.insertionCounter
    },
    sortIndividualsNewFirst(items) {
      return items.slice().sort((a, b) => {
        const ka = this.getItemKey(a)
        const kb = this.getItemKey(b)
        const oa = ka ? this.insertionOrderMap[ka] : undefined
        const ob = kb ? this.insertionOrderMap[kb] : undefined
        if (oa === undefined && ob === undefined) return 0
        if (oa === undefined) return 1 // items without explicit order go after newly inserted
        if (ob === undefined) return -1
        return ob - oa // newer (higher counter) first
      })
    },
    preserveSelectedScroll(updateFn) {
      // Keep current scroll position of the selected items scroller during list updates
      const scroller = this.$refs.selectedScroller && this.$refs.selectedScroller.$el
      const currentScrollTop = scroller ? scroller.scrollTop : null

      updateFn()

      if (currentScrollTop !== null) {
        this.$nextTick(() => {
          // Restore scrollTop; guard if scroller was re-rendered
          const el = this.$refs.selectedScroller && this.$refs.selectedScroller.$el
          if (el) el.scrollTop = currentScrollTop
        })
      }
    },
    isGroup(item) {
      return item.__typename === 'ProductsGroup'
    },
    uploadCsv() {
      this.$refs.inputFile.activateInput()
    },
    selectAllSearchedItems() {
      if (!this.isSelectAllEnabled) {
        return
      }
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems) {
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
        return
      }
      let numberOfItemsToInsert = this.searchedItems.length
      if (this.isLengthLimited && numberOfItemsToInsert + this.formattedChosenItems.length >= this.maxSelectedItems) {
        numberOfItemsToInsert = Math.min(numberOfItemsToInsert, this.maxSelectedItems - this.formattedChosenItems.length)
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido` })
      }
      const itemsToInsert = this.searchedItems.splice(0, numberOfItemsToInsert)
      // TODO: Groups - if (!itemObject.isGroup) pushList.push(itemObject)

      itemsToInsert.forEach((it) => this.markInserted(it))
      this.chosenItems.push(...itemsToInsert)
      if (this.chosenItems.length > 1000) this.showProcessingIndicator = true
      this.clearCache()
      this.updateGroupList()

      // Process immediately to keep UI static (even for large lists)
      this.$nextTick(() => {
        // Access formattedChosenItems to trigger immediate processing
        // This ensures the cache is updated synchronously and UI stays static
        void this.formattedChosenItems
      })
    },
    updatedFileInput() {
      const fileType = this.$refs.inputFile.fileType
      if (!this.fileContent) return
      if (!this.fileTypes.includes(fileType)) {
        this.$snackbar({ message: `Permitido apenas arquivos em .csv, o arquivo tem o formato: ${fileType}`, snackbarColor: 'error' })
        return
      }
      const idList = CSV.parse(this.fileContent)
        .map((line) => line[0])
        .filter((element) => /^\d+$/.test(element.trim()))
      if (!idList.length) {
        this.$snackbar({ message: `Não foi possível importar os dados do arquivo`, snackbarColor: 'error' })
        return
      }
      this.getSelectedProducts(idList)
    },
    setSelectedItems() {
      // Only process if we have selected products and the modal is open
      if (!this.selectedProducts.length || this.window !== 1) {
        return
      }

      this.chosenItems = []
      this.chosenGroupsItems = []
      this.clearCache()

      // Check if we have persistent cache for instant loading
      const currentCacheKey = this.generateCacheKey()

      // FORCE VALIDATION: Always be suspicious of cache, especially after confirmations
      // This is critical for .sync scenarios where watchers may not fire while modal is closed
      const productCountChanged = this.chosenItemsLength > 0 && this.chosenItemsLength !== this.selectedProducts.length
      const cacheKeyChanged = this.cacheKey && this.cacheKey !== currentCacheKey
      const cacheVersionMismatch = this.persistentCache && this.persistentCache.version !== this.cacheVersion

      // IMPORTANT: If cache version doesn't match, ALWAYS reprocess
      // This ensures that after any confirmation, cache is invalidated and we process fresh
      // Deep check is secondary - version mismatch is primary indicator
      const shouldInvalidateCache = cacheKeyChanged || productCountChanged || cacheVersionMismatch || !this.cacheKey

      if (shouldInvalidateCache) {
        this.clearPersistentCache()
        // Reset chosenItemsLength to ensure fresh processing
        this.chosenItemsLength = 0
      }

      // STRICT CACHE VALIDATION:
      // Only use cache if ALL of these are true:
      // 1. Cache exists
      // 2. Version matches (CRITICAL - means no confirmation happened since cache was created)
      // 3. Key matches (same set of products by ID/SKU)
      // 4. Product count matches (same number of items)
      // 5. We actually have a valid cache key (not first time)
      //
      // If ANY of these fail, especially version mismatch, we MUST reprocess
      // Version mismatch means cache was invalidated by a confirmation and is stale
      const canUseCache =
        this.persistentCache &&
        this.persistentCache.version === this.cacheVersion &&
        this.cacheKey === currentCacheKey &&
        this.cacheKey &&
        !productCountChanged &&
        this.persistentCache.data &&
        this.persistentCache.data.length > 0

      if (canUseCache) {
        // All validations passed - safe to use cache
        this.formattedChosenItemsCache = this.persistentCache.data
        this.chosenItemsLength = this.selectedProducts.length

        // IMPORTANT: We still need to populate chosenItems for the UI to work
        // The persistent cache only contains the formatted data, not the raw chosenItems
        this.processSelectedProducts()
        return
      }

      // For very large lists, process in batches
      if (this.selectedProducts.length > 2000) {
        this.showProcessingIndicator = true
        // Process first 1000 items immediately
        const firstBatch = this.selectedProducts.slice(0, 1000)
        this.processSelectedProductsBatch(firstBatch)

        // Process remaining items asynchronously
        if (this.selectedProducts.length > 1000) {
          this.debouncedOperation(() => {
            const remainingBatch = this.selectedProducts.slice(1000)
            this.processSelectedProductsBatch(remainingBatch)
          }, 100)
        }
      } else {
        // For smaller lists, process all items immediately
        this.processSelectedProducts()
      }

      // Save current product count AFTER processing for next comparison
      // This ensures we can detect changes when modal opens again
      this.chosenItemsLength = this.selectedProducts.length
    },
    processSelectedProducts() {
      this.selectedProducts.forEach((product, index) => {
        if (product.products && Array.isArray(product.products) && product.products.length > 0) {
          // This is a group with products (loaded from saved data)
          this.reconstructGroupFromSaved(product)
        } else if (product.isGroup) {
          // This is a group item (individual product from a group)
          this.chosenItems.push(product)
        } else {
          // This is a regular product - add directly to chosenItems
          this.chosenItems.push(product)
        }
      })

      // Force update to ensure UI reflects the changes
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    processSelectedProductsBatch(products) {
      products.forEach((product) => {
        if (product.products && Array.isArray(product.products) && product.products.length > 0) {
          // This is a group with products (loaded from saved data)
          this.reconstructGroupFromSaved(product)
        } else if (product.isGroup) {
          // This is a group item (individual product from a group)
          this.chosenItems.push(product)
        } else {
          // This is a regular product - add directly to chosenItems
          this.chosenItems.push(product)
        }
      })
    },
    reconstructGroupFromSaved(group) {
      // Reconstruct group from saved format to the format expected by chosenItems
      // Saved format: { isGroup: true, _id, name, products: [...] }
      // Expected format: individual items with isGroup: true, nameGroup, groupInfo

      const { name, _id, __typename, image_url, rules_exhibition, categories_final_consumer, disabled, ...groupRest } = group

      // Create groupInfo structure
      const groupInfo = {
        _id,
        __typename: __typename || 'ProductsGroup',
        image_url,
        rules_exhibition,
        categories_final_consumer,
        ...groupRest,
      }

      // Convert each product in the group to the expected format
      const productsFormatted = group.products.map((product) => {
        return {
          ...product,
          _id: product._id || product.sku, // Ensure _id is always defined for DynamicScroller
          isGroup: true,
          nameGroup: name,
          name: product.name || '',
          sku: product.sku || '',
          disabled: disabled !== undefined ? disabled : false,
          groupInfo,
          product: {
            image: product.image || product.image_url,
            ean: product.ean,
            eans: product.eans?.length > 1 ? product.eans : [product.ean],
          },
        }
      })

      // Add all products from the group to chosenItems
      this.chosenItems.push(...productsFormatted)
    },
    querySelections(query) {
      this.searchValue = query
      if (!query || query.length < 2) return
      this.loading = true
      const self = this
      window.clearTimeout(this.timeout)
      this.timeout = window.setTimeout(() => {
        self.searchItem(query)
      }, 500)
    },
    searchItem(query) {
      const variables = { search: query }
      if (this.isOffersByUnit) {
        variables.units = this.campaign.units
      }
      if (this.onlyActive) {
        variables.active = true
      }
      if (this.sortByRelevance) {
        variables.sort = ['eanFullMatch', 'skuFullMatch', 'active', 'relevance_score', 'name']
        variables.sort_direction = ['DESC', 'DESC', 'DESC', 'DESC', 'ASC']
      }
      if (this.stateFilters.measureUnit) {
        variables.measure_acronym = this.stateFilters.measureUnit
      }
      this.$apollo
        .query({
          query: QUERY_GET_PRODUCTS_AND_GROUPS,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { products: { products } = {}, productsGroups: { productsGroups } } = {} }) => {
          if (!this.disableGroups) {
            this.productsGroups = this.removeChosenGroups(productsGroups)
          }
          this.searchedItems = this.removeChosenItems(products)
          this.loading = false
        })
    },
    clearAll() {
      this.loading = true
      this.chosenItems = this.chosenItems.filter((group) => group.disabled)
      this.chosenGroupsItems = this.chosenGroupsItems.filter((group) => group.disabled)
      this.clearCache()
      this.clearPersistentCache() // Clear persistent cache when clearing all items

      if (this.searchValue) this.searchItem(this.searchValue)
      else this.loading = false
    },
    setItem(item, removeList, pushList, isRemoving = false) {
      const run = () => {
        if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems && !isRemoving) {
          this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
          return
        }

        const { sku } = item
        const itemObject = find(removeList, { sku })
        if (!itemObject) return

        const itemObjectIndex = findIndex(removeList, itemObject)
        removeList.splice(itemObjectIndex, 1)

        // Only add to pushList if we're not removing (i.e., we're adding a product)
        if (!isRemoving) {
          if (!itemObject.isGroup) {
            this.markInserted(itemObject)
            pushList.push(itemObject)
          } else {
            const groupId = itemObject.groupInfo._id
            const alreadyExists = this.productsGroups.some((group) => group._id === groupId)

            if (!alreadyExists) {
              const groupToAdd = {
                ...itemObject.groupInfo,
                name: itemObject.nameGroup,
              }

              this.productsGroups.push(groupToAdd)
              this.productsGroups = [...this.productsGroups]
            }
          }
        } else {
          // When removing, only add back to search results if it's a non-grouped product
          // Products inside groups should NOT be added back to search results
          if (!itemObject.isGroup) {
            // For regular (non-grouped) products, add back to searchedItems
            this.searchedItems.push(itemObject)
          }
          // If itemObject.isGroup === true, it's a product inside a group
          // Don't add it back to search results - just remove it from chosenItems
        }

        // If removing, avoid showing processing indicator for large lists
        if (isRemoving) this.showProcessingIndicator = false
        this.clearCache()
        this.clearPersistentCache() // Clear persistent cache when removing items
        this.updateGroupList()

        // Process immediately to keep UI static (even for large lists)
        this.$nextTick(() => {
          // Access formattedChosenItems to trigger immediate processing
          // This ensures the cache is updated synchronously and UI stays static
          void this.formattedChosenItems
        })
      }

      // Preserve scroll when updating selected list
      this.preserveSelectedScroll(run)
    },
    async checkGroupProducts(group, removeList, pushList) {
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems && !this.chosenLoader) {
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
        return
      }

      let newGroup = group
      if (!group.products) {
        this.chosenLoader = true
        const {
          data: { productsGroup },
        } = await this.getGroupProducts(group._id)
        newGroup = productsGroup
        this.chosenLoader = false
      }

      this.setGroup(newGroup, removeList, pushList)
    },
    async getGroupProducts(id) {
      try {
        const variables = { _id: id }
        if (this.onlyActive) {
          variables.active = true
        }
        return await this.$apollo.query({
          query: QUERY_GET_GROUP_PRODUCTS,
          variables: {
            _id: id,
          },
        })
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar produtos.', snackbarColor: 'error' })
      }
    },
    setGroup(group, removeList, pushList) {
      const itemGroup = find(removeList, {
        _id: group._id,
      })

      if (!itemGroup) return
      const itemGroupIndex = findIndex(removeList, itemGroup)
      const { name, ...groupInfo } = removeList[itemGroupIndex]
      const productsFormatted = group.products.map((product) => {
        return {
          ...product,
          _id: product._id || product.sku, // Ensure _id is always defined for DynamicScroller
          isGroup: true,
          nameGroup: name,
          name: product.name || '',
          sku: product.sku || '',
          groupInfo,
          product: {
            image: product.image,
            ean: product.ean,
            eans: product.eans?.length > 1 ? product.eans : [product.ean],
          },
        }
      })
      removeList.splice(itemGroupIndex, 1)
      pushList.push(...productsFormatted)

      this.clearCache()
      this.updateGroupList()

      // Process immediately to keep UI static (even for large lists)
      this.$nextTick(() => {
        // Access formattedChosenItems to trigger immediate processing
        // This ensures the cache is updated synchronously and UI stays static
        void this.formattedChosenItems
      })
    },
    updateGroupList() {
      this.chosenGroupsItems = this.formattedChosenItems
    },
    removeGroup(group, removeList, pushList) {
      const run = () => {
        const groupId = group?._id

        this.productsGroups.push(group)

        const productIdsInGroup = new Set(group.products.map((p) => p._id))

        const newRemoveList = removeList.filter((item) => {
          if (!item) return true

          const isGroupItem = item.isGroup === true && (item._id === groupId || item.groupInfo?._id === groupId)
          const isProductInGroup = !item.isGroup && productIdsInGroup.has(item._id)

          return !isGroupItem && !isProductInGroup
        })

        removeList.splice(0, removeList.length, ...newRemoveList)

        this.clearCache()
        this.clearPersistentCache() // Clear persistent cache when removing groups
        this.updateGroupList()

        // Process immediately to keep UI static (even for large lists)
        this.$nextTick(() => {
          // Access formattedChosenItems to trigger immediate processing
          // This ensures the cache is updated synchronously and UI stays static
          void this.formattedChosenItems
        })
      }
      this.showProcessingIndicator = false
      this.preserveSelectedScroll(run)
    },
    removeChosenItems(responseData) {
      const array = responseData
      for (let item of this.chosenItems) {
        let itemIndex = array.findIndex((product) => product.sku === item.sku)
        if (itemIndex > -1) {
          array.splice(itemIndex, 1)
        }
      }

      return array
    },
    removeChosenGroups(responseData) {
      const array = responseData
      return array.reduce((accumulator, current) => {
        const wasChose = this.chosenItems.map((product) => product.nameGroup).includes(current.name)

        if (!wasChose) {
          accumulator.push(current)
        }

        return accumulator
      }, [])
    },
    cancel() {
      // Cancel any ongoing processing immediately
      this.isProcessingItems = false
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.$emit('cancel')
      this.clearData()
    },
    confirmChanges(productsArray, productsAndGroupsArray) {
      const items = {
        products: productsArray,
        productsAndGroups: productsAndGroupsArray,
      }
      this.$emit('confirm-changes', items)
      this.clearData()
    },
    clearData() {
      this.searchValue = ''
      this.searchedItems = []
      this.chosenItems = []
      this.chosenGroupsItems = []
      this.productsGroups = []
      this.clearPersistentCache() // Clear persistent cache when data changes
      this.optimizeForClosing()
    },
    checkValuesAndConfirmChanges() {
      let productsArray = []

      // IMPORTANT: Use only the current in-modal state (chosenItems) on save.
      const sourceProducts = this.chosenItems

      if (this.filterDuplicateProducts) {
        productsArray = sourceProducts.reduce((accumulator, currentProduct) => {
          const position = accumulator.findIndex((item) => item.sku && item.sku === currentProduct.sku)

          if (position === -1) {
            accumulator.push(currentProduct)
          }

          return accumulator
        }, [])
      } else {
        productsArray = [...sourceProducts] // Create a copy to ensure all items are included
      }

      // Update chosenGroupsItems to match the confirmed products
      this.chosenGroupsItems = this.formattedChosenItems

      // Increment cache version to invalidate any existing cache after confirmation
      // This ensures next modal open will process fresh data, not stale cache
      this.cacheVersion++
      this.clearPersistentCache()

      this.confirmChanges(productsArray, this.chosenGroupsItems)
    },
    async getSelectedProducts({ eans = [], skus = [] }) {
      this.chosenLoader = true
      try {
        const variables = {
          eans,
          skus,
          roomId: this.roomId,
        }
        if (this.isOffersByUnit) {
          variables.units = this.campaign.units
        }

        await this.$apollo.query({
          query: QUERY_ACTIVATE_IMPORTED_PRODUCTS,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['asyncJobs'],
          },
        })
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
        this.chosenLoader = false
      }
    },
    toggleGroupExpand(groupId) {
      this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId])
    },
    getVisibleGroupProducts(group) {
      // Return all products for groups - the DynamicScroller will handle virtualization
      // Ensure all products have _id for DynamicScroller key-field
      return (group.products || []).map((product) => ({
        ...product,
        _id: product._id || product.sku,
      }))
    },
    isGroupExpanded(groupId) {
      return !!this.expandedGroups[groupId]
    },
    clearCache() {
      this.formattedChosenItemsCache = null
      this.chosenItemsLength = 0
      this.isProcessingItems = false
      // Don't clear persistent cache - keep it for instant loading
    },
    updateFormattedChosenItemsCache(result) {
      this.formattedChosenItemsCache = result
      this.chosenItemsLength = this.chosenItems.length

      // Save to persistent cache for instant loading next time
      // Only save if we have more items than current cache or if cache is empty
      if (result.length > 0 && (!this.persistentCache || !this.persistentCache.data || result.length > this.persistentCache.data.length)) {
        this.saveToPersistentCache(result)
      }
    },
    processItemsSync() {
      const groupedMap = new Map()
      const individualItems = []

      // Process all items efficiently in one pass
      for (const item of this.chosenItems) {
        if (item.isGroup) {
          const groupId = item.groupInfo._id
          if (!groupedMap.has(groupId)) {
            groupedMap.set(groupId, {
              name: item.nameGroup,
              disabled: item.disabled || false,
              isGroup: true,
              _id: groupId,
              __typename: item.groupInfo.__typename,
              image_url: item.groupInfo.image_url,
              rules_exhibition: item.groupInfo.rules_exhibition,
              categories_final_consumer: item.groupInfo.categories_final_consumer,
              products: [],
            })
          }
          groupedMap.get(groupId).products.push(item)
        } else {
          individualItems.push({
            ...item,
            _id: item._id || item.sku,
          })
        }
      }

      const result = [...Array.from(groupedMap.values()), ...this.sortIndividualsNewFirst(individualItems)]
      this.updateFormattedChosenItemsCache(result)
      return result
    },
    beforeDestroy() {
      // Cancel any ongoing processing
      this.isProcessingItems = false
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      // Clear all caches to free memory
      this.clearCache()
    },
    // Optimized method for modal closing
    optimizeForClosing() {
      // Clear heavy data structures but keep persistent cache
      this.formattedChosenItemsCache = null
      this.isProcessingItems = false
      this.debounceTimer = null
    },
    // Pause processing when modal is not visible
    pauseProcessing() {
      this.isProcessingItems = false
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
    },
    // Generate cache key based on selected products
    generateCacheKey() {
      if (!this.selectedProducts.length) return null
      return this.selectedProducts
        .map((p) => p._id || p.sku)
        .sort()
        .join(',')
    },
    // Save to persistent cache for instant loading next time
    saveToPersistentCache(data) {
      this.persistentCache = {
        data,
        version: this.cacheVersion, // Save current version with cache
      }
      this.cacheKey = this.generateCacheKey()
    },
    // Clear persistent cache
    clearPersistentCache() {
      this.persistentCache = null
      this.cacheKey = null
    },
    // Preload data in background for instant opening
    preloadData() {
      if (this.selectedProducts.length > 0) {
        // Process data in background without blocking UI
        this.$nextTick(() => {
          // First populate chosenItems from selectedProducts
          this.populateChosenItemsFromSelectedProducts()
          // Then process the formatted data
          const result = this.processItemsSync()
          // Only save to persistent cache if we have more items than current cache
          if (result.length > (this.persistentCache && this.persistentCache.data ? this.persistentCache.data.length : 0)) {
            this.saveToPersistentCache(result)
          }
        })
      }
    },
    // Helper method to populate chosenItems from selectedProducts
    populateChosenItemsFromSelectedProducts() {
      this.chosenItems = []
      this.selectedProducts.forEach((product) => {
        if (product.products && Array.isArray(product.products) && product.products.length > 0) {
          // This is a group with products (loaded from saved data)
          this.reconstructGroupFromSaved(product)
        } else if (product.isGroup) {
          // This is a group item (individual product from a group)
          this.chosenItems.push(product)
        } else {
          // This is a regular product - add directly to chosenItems
          this.chosenItems.push(product)
        }
      })
    },
    // Debounced method for heavy operations
    debouncedOperation(operation, delay = 100) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(operation, delay)
    },
  },
}
</script>

<style lang="scss" scoped>
.rotated {
  transform: rotate(180deg);
  transition: transform 0.3s;
}
.group-products-scroller {
  max-height: 200px;
  overflow-y: auto;
  will-change: transform;
  contain: layout style paint;
}
.select-products-dialog {
  display: grid;
  grid-template-columns: 1fr 1fr;

  .container-subtitle {
    font-size: 16px;
    font-weight: 400;
    color: $cinza-d10;
    line-height: 19.2px;
  }

  .filter-button {
    padding-bottom: 2px;
  }

  .left-side {
    background-color: $cinza-l90;
  }

  .right-side {
    background-color: $cinza-l80;
  }

  .products-box {
    margin-top: 16px;
    width: 100%;
    height: 264px;
    overflow: hidden;
    .products-box-empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      height: 100%;

      p {
        margin: 0;
        font-size: 14px;
        color: $cinza-l20;
      }
    }

    .scroller {
      height: 264px !important;
      overflow-y: auto !important;
      will-change: transform;
      contain: layout style paint;
    }

    .selected-items-scroller {
      height: 214px !important;
      overflow-y: auto !important;
      will-change: transform;
      contain: layout style paint;
    }

    .result-list-item {
      padding: 12px 12px 12px 24px;
      gap: 12px;
      cursor: pointer;
      border-bottom: 1px solid $cinza-l70;
      &:hover {
        background-color: $cinza-l80;
      }

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 405px;
      }
      .product-name-container {
        gap: 12px;
      }
    }
  }

  .right-side-items-list {
    .clear-all {
      height: 50px;
      padding: 16px;
      font-size: 14px;
      color: $cinza-0;
      text-decoration: underline;
      cursor: pointer;
    }

    .processing-indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      color: $cinza-0;
      font-size: 14px;
    }

    .normal-product {
      width: 100%;
      padding: 0 12px;
      p {
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 416px;
        color: $cinza-d10;
        font-size: 14px;
      }

      span {
        cursor: pointer;
      }
    }

    .group-container {
      background-color: $cinza-l70;
    }
  }
}
</style>
